import { useState } from 'react';
import { Plus, Settings, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChannelWizard, ChannelCard, DeleteChannelDialog, EditChannelDialog } from '@/components/channels';
import { useChannelConnections } from '@/hooks/useChannelConnections';

/**
 * Channels List Page
 *
 * Lista todas as conexões de canais configuradas para a conta
 * Mobile-first design com cards responsivos
 */
export default function ChannelsPage() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedChannelForDelete, setSelectedChannelForDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedChannelForEdit, setSelectedChannelForEdit] = useState<string | null>(null);

  const { data: response, isLoading, refetch } = useChannelConnections();

  const channels = response?.connections || [];

  const handleDeleteClick = (channelId: string) => {
    const channel = channels.find((c) => c.id === channelId);
    if (channel) {
      setSelectedChannelForDelete({ id: channel.id, name: channel.name });
      setDeleteDialogOpen(true);
    }
  };

  const handleEditClick = (channelId: string) => {
    setSelectedChannelForEdit(channelId);
    setEditDialogOpen(true);
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      {/* Channel Wizard */}
      <ChannelWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={() => refetch()}
      />

      {/* Edit Channel Dialog */}
      <EditChannelDialog
        channelId={selectedChannelForEdit}
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedChannelForEdit(null);
        }}
        onSuccess={() => refetch()}
      />

      {/* Delete Channel Dialog */}
      <DeleteChannelDialog
        channelId={selectedChannelForDelete?.id || null}
        channelName={selectedChannelForDelete?.name}
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedChannelForDelete(null);
        }}
        onSuccess={() => refetch()}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Canais</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie suas conexões com provedores de mensagens
          </p>
        </div>
        <Button onClick={() => setWizardOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Canal
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && channels.length === 0 && (
        <Card className="text-center py-12">
          <CardContent className="pt-6">
            <Settings className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum canal configurado</h3>
            <p className="text-muted-foreground mb-6">
              Configure seu primeiro canal para começar a enviar e receber mensagens
            </p>
            <Button onClick={() => setWizardOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Canal
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Channels Grid */}
      {!isLoading && channels.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.map((channel) => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
